// src/components/analytics/RevenueTrendChart.js
import React from "react";
import { Card, CardHeader, CardContent, Box, Typography } from "@material-ui/core";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const DEFAULT_CHART_COLOR = "#0a2540";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);

const compactCurrencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(v || 0);

const formatDate = (value) => {
  if (!value) return "Unknown date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "Unknown date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatTickDate = (value, grouping) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(+date)) return "";
  if (grouping === "month") return date.toLocaleDateString(undefined, { month: "short" });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const buildGradientId = (title = "revenue-trend") =>
  `stripe-clean-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "chart"}`;

const getYAxisDomain = (data, valueFor) => {
  if (!data.length) return [0, 100];
  const values = data.map(valueFor).filter((value) => Number.isFinite(value));
  if (!values.length) return [0, 100];
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const spread = Math.max(max - min, Math.abs(max), 100);
  const padding = spread * 0.08;
  return [min < 0 ? Math.floor(min - padding) : 0, Math.ceil(max + padding)];
};

const ChartTooltip = ({ active, payload, currency, grouping, mode, valueField, hidePayments, valueFor }) => {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload || {};
  const payments = point.payments || [];
  const invoices = point.invoiceDetails || [];
  const isAccrual = mode === "accrual";
  const isSimpleValue = grouping === "month" || valueField !== "revenue" || hidePayments;
  const value = valueFor(point);

  return (
    <Box
      style={{
        minWidth: 220,
        maxWidth: 360,
        maxHeight: 320,
        overflowY: "auto",
        padding: "12px 14px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 14,
        background: "rgba(255, 255, 255, 0.98)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
      }}
    >
      <Typography variant="caption" style={{ color: "#64748b", fontWeight: 700, letterSpacing: 0.2 }}>
        {formatDate(point.period)}
      </Typography>
      <Typography variant="h6" style={{ color: "#0f172a", fontWeight: 800, lineHeight: 1.2 }}>
        {currencyFmt(value, currency)}
      </Typography>

      {isSimpleValue ? null : !isAccrual ? (
        <Box mt={1}>
          <Typography variant="subtitle2" style={{ color: "#0f172a", fontWeight: 800 }}>
            Payments ({payments.length})
          </Typography>
          {payments.length === 0 ? (
            <Typography variant="caption" style={{ color: "#64748b" }}>No payment details.</Typography>
          ) : (
            payments.map((pay, payIdx) => (
              <Box key={payIdx} mt={0.75}>
                <Typography variant="body2" style={{ color: "#0f172a", fontWeight: 700 }}>
                  {pay.paidBy || "Payment"}
                </Typography>
                <Typography variant="caption" style={{ color: "#64748b" }}>
                  {currencyFmt(pay.amount, currency)}
                  {pay.paymentMethod ? ` • ${pay.paymentMethod}` : ""}
                  {pay.date ? ` • ${formatDate(pay.date)}` : ""}
                </Typography>
                {pay.note && (
                  <Typography variant="caption" style={{ display: "block", color: "#64748b" }}>
                    {pay.note}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      ) : (
        <Box mt={1}>
          <Typography variant="subtitle2" style={{ color: "#0f172a", fontWeight: 800 }}>
            Invoices ({invoices.length})
          </Typography>
          {invoices.length === 0 ? (
            <Typography variant="caption" style={{ color: "#64748b" }}>No invoice details.</Typography>
          ) : (
            invoices.map((inv, idx) => (
              <Box key={idx} mt={0.75}>
                <Typography variant="body2" style={{ color: "#0f172a", fontWeight: 700 }}>
                  {inv.clientStore || "Invoice"}
                </Typography>
                <Typography variant="caption" style={{ color: "#64748b" }}>
                  {currencyFmt(inv.amount, currency)}
                  {inv.dueDate ? ` • Due ${formatDate(inv.dueDate)}` : ""}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default function RevenueTrendChart({
  title = "Revenue Trend",
  subheader,
  series = [],
  currency = "USD",
  totalRevenue = 0,
  mode = "cash",
  grouping = "day",
  valueField = "revenue",
  areaColor,
  hidePayments = false,
  totalLabel = "Total Revenue",
}) {
  const chartColor = areaColor || DEFAULT_CHART_COLOR;
  const gradientId = React.useMemo(() => buildGradientId(title), [title]);
  const valueFor = React.useCallback((point) => Number(point?.[valueField]) || 0, [valueField]);

  const data = React.useMemo(
    () =>
      (series || [])
        .map(d => {
          const period = new Date(d.period);
          const normalizedPoint = {
            ...d,
            period,
            periodTs: Number.isNaN(+period) ? null : period.getTime(),
            revenue: Number(d.revenue) || 0,
            [valueField]: Number(d[valueField]) || 0,
            payments: (d.payments || []).map(p => ({
              ...p,
              date: p.date ? new Date(p.date) : null,
              amount: Number(p.amount) || 0,
            })),
            invoiceDetails: (d.invoiceDetails || []).map(inv => ({
              ...inv,
              clientStore: inv.clientStore || inv.clientName || null,
              dueDate: inv.dueDate ? new Date(inv.dueDate) : null,
              receivedByDate: inv.receivedByDate ? new Date(inv.receivedByDate) : null,
              amount: Number(inv.amount) || 0,
            })),
          };
          return normalizedPoint;
        })
        .filter((point) => point.periodTs !== null)
        .sort((a, b) => a.periodTs - b.periodTs),
    [series, valueField]
  );

  const yDomain = React.useMemo(() => getYAxisDomain(data, valueFor), [data, valueFor]);
  const showDots = data.length <= 45;

  return (
    <Card
      variant="outlined"
      style={{
        height: "100%",
        border: "1px solid #e6ebf1",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 16px 45px rgba(15, 23, 42, 0.06)",
      }}
    >
      <CardHeader
        style={{ padding: "18px 22px 8px" }}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%" style={{ gap: 16 }}>
            <Box>
              <Typography variant="h6" style={{ color: "#0f172a", fontWeight: 800, letterSpacing: -0.2 }}>
                {title}
              </Typography>
              {subheader && (
                <Typography variant="caption" style={{ color: "#64748b", fontWeight: 600 }}>
                  {subheader}
                </Typography>
              )}
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" style={{ color: "#64748b", fontWeight: 700, letterSpacing: 0.2 }}>
                {totalLabel}
              </Typography>
              <Typography variant="h5" style={{ color: "#0a2540", fontWeight: 850, lineHeight: 1.15 }}>
                {currencyFmt(totalRevenue, currency)}
              </Typography>
            </Box>
          </Box>
        }
      />
      <CardContent style={{ height: 350, padding: "8px 18px 18px" }}>
        {data.length === 0 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            style={{ color: "#64748b", background: "#f8fafc", borderRadius: 16 }}
          >
            No data in this range.
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 18, right: 20, left: 8, bottom: 8 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.22} />
                  <stop offset="52%" stopColor={chartColor} stopOpacity={0.08} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#eef2f7" strokeDasharray="4 8" />
              <XAxis
                dataKey="periodTs"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(value) => formatTickDate(value, grouping)}
                axisLine={false}
                tickLine={false}
                tickMargin={12}
                minTickGap={28}
                tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
              />
              <YAxis
                domain={yDomain}
                axisLine={false}
                tickLine={false}
                tickMargin={12}
                width={76}
                tickFormatter={(value) => compactCurrencyFmt(value, currency)}
                tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
              />
              <Tooltip
                content={(
                  <ChartTooltip
                    currency={currency}
                    grouping={grouping}
                    mode={mode}
                    valueField={valueField}
                    hidePayments={hidePayments}
                    valueFor={valueFor}
                  />
                )}
                cursor={{ stroke: chartColor, strokeWidth: 2, strokeOpacity: 0.12 }}
                wrapperStyle={{ outline: "none" }}
              />
              <Area
                type="monotone"
                dataKey={valueField}
                stroke={chartColor}
                strokeWidth={3}
                fill={`url(#${gradientId})`}
                fillOpacity={1}
                dot={showDots ? { r: 2.4, fill: "#ffffff", stroke: chartColor, strokeWidth: 2 } : false}
                activeDot={{ r: 5, fill: "#ffffff", stroke: chartColor, strokeWidth: 3 }}
                isAnimationActive
                animationDuration={650}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
